import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ProfileField, ProfileDataType } from '../../types/profile';
import { connectionService, invitationService, profileService } from '../../services';
import { Invite } from '../../types/invites';
import UserDataForm from '../../components/forms/userData/UserDataForm';
import useProfileFields from '../../hooks/useProfileFields';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import { joinWithAnd, profileDataTypeToString } from '../../utils/stringUtils';
import useDeleteParams from '../../hooks/useDeleteParams';
import { SuccessPageState } from '../../types/navigation';
import { getUserId } from '../../services/httpService';
import NoDataSharedModal from '../../components/modals/NoDataSharedModal';
import Button from '../../components/misc/Button';

export default function AuthenticatedInvitePage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const profileFields = useProfileFields();
  useDeleteParams('link');

  const invitationId = params.get('invitationId');

  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [newFields, setNewFields] = useState<Partial<Record<ProfileDataType, ProfileField>>>({});
  const [invite, setInvite] = useState<Invite>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!invitationId) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
      return;
    }

    invitationService
      .getInvite(invitationId)
      .then((inv) => setInvite(inv))
      .catch(() => {
        toast.error(t('toast.error.general.inviteNotFound'));
        navigate('/');
      });
  }, []);

  useEffect(() => {
    if (!invite || profileFields.length === 0) return;

    const loggedInUserId = getUserId();
    if (+invite.inviterId === +loggedInUserId) {
      //this is the loggedin user own invite
      toast.warning('Je kan niet reageren op je eigen link');
      navigate('/');
    }

    connectionService
      .getSharedDataWithUser(invite.inviterId)
      .then((data) => {
        setSelectedData(data);
      })
      .catch((): void => {});
  }, [invite, profileFields]);

  const handleComplete = (): void => {
    if (selectedData.length === 0) setModalOpen(true);
    else handleSubmit();
  };

  const handleSubmit = async (): Promise<void> => {
    // Save new fields first if any exist
    let allSelectedData = [...selectedData];
    const newFieldsToSave = Object.values(newFields).filter((field) => field && !field.id);

    if (newFieldsToSave.length > 0) {
      try {
        const savedFields = await profileService.addFields(newFieldsToSave);
        allSelectedData = [...selectedData, ...savedFields];
      } catch (error) {
        toast.error(t('toast.error.general.saveFailed'));
        return;
      }
    }

    await connectionService.createConnection(
      invitationId!,
      allSelectedData.map((x) => x.id!),
    );

    const state: SuccessPageState = {
      translationKey: 'page.common.success.message.invite',
      translationOptions: {
        requesterName: requesterName || '',
      },
      requesterId: invite?.inviterId,
      requesterAlias: invite?.inviterAlias,
    };
    navigate('/success', { state });
    toast.success('Verbinding aangemaakt');
  };

  const requesterName = invite?.inviterAlias?.split('+')[0];

  return (
    <>
      <ResponsePageWrapper>
        <div className="flex h-full flex-1 flex-col gap-4">
          <div className="flex flex-col">
            <h2 className="mb-4 text-2xl font-semibold">{t('page.shared.requestedData')}</h2>
            <p className="text-sm">
              {invite?.propertyTypeRequested.length === 0
                ? t('page.invite.businessCardMessage', { name: requesterName })
                : t('page.invite.message', {
                    name: requesterName,
                    dataRequest: joinWithAnd(invite?.propertyTypeRequested.map(profileDataTypeToString) || []),
                  })}{' '}
              {t('page.invite.selectData')}
            </p>
          </div>

          <UserDataForm
            selectedData={selectedData || []}
            setSelectedData={setSelectedData}
            newFields={newFields}
            onNewFieldsChange={setNewFields}
            hasSelectAll
          />
          <div className="flex h-full min-h-10 justify-end">
            <Button type="button" variant="primary" onClick={handleComplete} className="px-[32px]">
              {t('page.shared.shareData')}
            </Button>
          </div>
        </div>
      </ResponsePageWrapper>
      <NoDataSharedModal
        open={modalOpen}
        setOpen={setModalOpen}
        requesterName={requesterName || ''}
        handleComplete={handleSubmit}
      />
    </>
  );
}
